import { useEffect } from "react";
import { usePrevNextController } from "ra-core";
import { useLocation, useNavigate } from "react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useSwipe } from "./useSwipe";

// LDF: In der aktuellen Listen-Selektion (Filter + Sortierung der Liste) vor- und zurückblättern – Buttons und Pfeiltasten ← →.

export const RecordPrevNext = ({
  filterDefaultValues,
  sort,
}: {
  filterDefaultValues?: Record<string, unknown>;
  sort?: { field: string; order: "ASC" | "DESC" };
}) => {
  const { hasPrev, hasNext, prevPath, nextPath, index, total, isPending } = usePrevNextController({
    linkType: "show",
    limit: 1000,
    filterDefaultValues,
    sort,
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Unterseite (Reiter wie /show/location) beim Blättern beibehalten
  const withTab = (path?: string) => {
    if (!path) return path;
    const tab = location.pathname.match(/\/show(\/[^/]+)$/)?.[1] ?? "";
    return `${path}${tab}`;
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable=true], [role=dialog], [role=tablist], [role=listbox], [role=menu]")) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowLeft" && prevPath) navigate(withTab(prevPath)!);
      if (e.key === "ArrowRight" && nextPath) navigate(withTab(nextPath)!);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevPath, nextPath, location.pathname]);

  // Handy: nach links wischen = nächster Eintrag, nach rechts = vorheriger
  useSwipe({
    onLeft: () => nextPath && navigate(withTab(nextPath)!),
    onRight: () => prevPath && navigate(withTab(prevPath)!),
  });

  if (isPending || total == null || index == null || index < 0) return null;

  return (
    <div className="flex items-center gap-1 text-xs text-muted-foreground" title="Mit ← → in der Auswahl blättern">
      <Button size="icon" variant="ghost" className="h-7 w-7" disabled={!hasPrev} onClick={() => prevPath && navigate(withTab(prevPath)!)}>
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="tabular-nums">
        {index + 1} / {total}
      </span>
      <Button size="icon" variant="ghost" className="h-7 w-7" disabled={!hasNext} onClick={() => nextPath && navigate(withTab(nextPath)!)}>
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};
